import { ActionKind, ActionTypes, evaluateCondition } from '@dynamic/ui';
import { t } from 'i18next';

import { Condition, MenuItem } from '#/client';
import { CommonAction } from '#/service/config';
import { ButtonVariant, NavigationType } from '#/service/constants';
import { InternalPageId, MenuItemDisplayType } from '#/service/constants/menu';
import { destinationRoutes } from '#/service/routes';
import { Dictionary, safeUrl } from '#/service/utils';

export const getNavigationTarget = (
  item: MenuItem,
  defaultSelectedItemId?: string
): NavigationTarget => {
  const { destinationId, destinationType } = getMenuItemInformation(item);

  const isHome = item?.id === defaultSelectedItemId;

  const pageUrl = isHome ? '/' : `/page/${destinationId}`;

  switch (destinationType) {
    case NavigationType.Page:
      return {
        internalUrl: pageUrl,
        exact: pageUrl === '/' ? true : undefined,
      };
    case NavigationType.Internal:
      return {
        // we need handle 'registration' route separately
        internalUrl:
          destinationRoutes[destinationId] ?? `/${destinationId.toLowerCase()}`,
      };
    case NavigationType.External:
      return {
        externalUrl: safeUrl(destinationId),
      };
    default:
      return {};
  }
};

interface NavigationTarget {
  internalUrl?: string;
  exact?: boolean;
  externalUrl?: string;
}

export const getDefaultMenuItem = (menuItems: MenuItem[]) => {
  return menuItems?.[0];
};

export const getDefaultMenuItemId = (menuItems: MenuItem[]) => {
  return getDefaultMenuItem(menuItems)?.id;
};

export const getMenuItemInformation = (menuItem?: MenuItem) => {
  const {
    display: {
      icon = null,
      type: displayType = '',
      title = '',
      buttonVariant = undefined,
    } = {},
    action: {
      params: {
        destination: {
          id: destinationId = '',
          type: destinationType = '',
        } = {},
      } = {},
    } = {},
  } = menuItem ?? {};

  return {
    destinationId,
    destinationType,
    displayType,
    title,
    icon,
    buttonVariant,
  };
};

export const findMenuItemByDestinationId = (
  items: MenuItem[],
  id: string
): MenuItem | undefined => {
  return items.find((el) => el.action?.params?.destination?.id === id);
};

export const getVisibleItems = (context: object, menuItems?: MenuItem[]) => {
  return (menuItems ?? []).filter((item) => {
    const { visibility } = item;

    return !visibility || evaluateCondition(visibility, context);
  });
};

export const getConditionalRoutes = (
  defaultMenuItemId: string,
  mainMenuItems?: MenuItem[],
  linkMenuItems?: MenuItem[]
): Dictionary<string, Condition> => {
  const conditionalRoutes: Dictionary<string, Condition> = {};

  [...(mainMenuItems ?? []), ...(linkMenuItems ?? [])].forEach((item) => {
    if (!item.visibility) {
      return conditionalRoutes;
    }

    const { internalUrl } = getNavigationTarget(item, defaultMenuItemId);

    if (internalUrl) {
      conditionalRoutes[internalUrl] = item.visibility;
    }
  });

  return conditionalRoutes;
};

export const getHardcodedLoginMenuItem = () => ({
  action: {
    kind: ActionKind.Login,
  },
  display: {
    buttonVariant: ButtonVariant.Ghost,
    title: t('button.login'),
    type: MenuItemDisplayType.Button,
  },
  id: 'hardcodedLoginButton',
  visibility: {
    subject: 'user.isLoggedIn',
    type: 'EQ',
    value: 'false',
  },
});

export const isLoginAction = (action: CommonAction) =>
  // v1
  (action.type === ActionTypes.Navigate &&
    action.params?.destination?.id === InternalPageId.Login) ||
  // v2
  action.kind === ActionKind.Login;
